import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { config } from '../config';

export enum ClientPlatform {
  All = 0,
  Website = 1,
  App = 2,
  Portal = 3,
  WebsiteAndApp = 4,
  WebsiteAndPortal = 5,
  AppAndPortal = 6
}

@Injectable({
  providedIn: 'root'
})
export class ClientPlatformService {
  private readonly expectedAndroidToken = config?.mobile_app_token;
  private appSession = false;
  private iosSession = false;
  private iosDetection: Promise<'ios'> | null = null;

  constructor(@Inject(PLATFORM_ID) private readonly platformId: any) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.appSession = this.hasValidAndroidToken();
  }

  isApp(): boolean {
    return this.appSession;
  }

  async detectPlatform(): Promise<'android' | 'ios' | 'website'> {
    if (!isPlatformBrowser(this.platformId)) {
      return 'website';
    }

    if (this.hasValidAndroidToken()) {
      this.appSession = true;
      return 'android';
    }
    if (this.iosSession) {
      return 'ios';
    }
    const bridge = window.webkit?.messageHandlers?.iOS;
    if (!bridge || typeof bridge.postMessage !== 'function') {
      return 'website';
    }
    if (this.iosDetection) {
      return this.iosDetection;
    }

    this.iosDetection = new Promise<'ios'>((resolve, reject) => {
      const previousReceiver = window.receiveToken;
      const cleanup = () => {
        clearTimeout(timer);
        if (window.receiveToken === receiveToken) {
          window.receiveToken = previousReceiver;
        }
      };
      const receiveToken = (token: string) => {
        cleanup();
        if (this.expectedAndroidToken && token === this.expectedAndroidToken) {
          this.iosSession = true;
          this.appSession = true;
          resolve('ios');
        } else {
          reject(new Error('The iOS app token could not be verified.'));
        }
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('The iOS app did not return its token. Please try again.'));
      }, 5000);

      // Register before requesting: native code may respond immediately.
      window.receiveToken = receiveToken;
      try {
        bridge.postMessage({ action: 'getToken' });
      } catch {
        cleanup();
        reject(new Error('Unable to request the iOS app token.'));
      }
    });

    try {
      return await this.iosDetection;
    } finally {
      this.iosDetection = null;
    }
  }

  getOrderPlatform(): ClientPlatform.Website | ClientPlatform.App {
    return this.isApp() ? ClientPlatform.App : ClientPlatform.Website;
  }

  isPromotionAvailable(platform: any): boolean {
    const promotionPlatform = Number(platform);
    const availablePlatforms = this.isApp()
      ? [
          ClientPlatform.All,
          ClientPlatform.App,
          ClientPlatform.WebsiteAndApp,
          ClientPlatform.AppAndPortal
        ]
      : [
          ClientPlatform.All,
          ClientPlatform.Website,
          ClientPlatform.WebsiteAndApp,
          ClientPlatform.WebsiteAndPortal
        ];

    return availablePlatforms.indexOf(promotionPlatform) !== -1;
  }

  filterPromotions(promotions: any): any[] {
    return Array.isArray(promotions)
      ? promotions.filter((promotion: any) => this.isPromotionAvailable(promotion && promotion.platform))
      : [];
  }

  private hasValidAndroidToken(): boolean {
    if (!window.Android || typeof window.Android.getToken !== 'function') {
      return false;
    }

    try {
      return window.Android.getToken() === this.expectedAndroidToken;
    } catch {
      return false;
    }
  }
}
